// 本地存储工具类

export interface StorageOptions {
  encrypt?: boolean
  prefix?: string
  expire?: number // 过期时间（毫秒）
}

// 存储项接口
interface StorageItem {
  data: any
  timestamp: number
  expire?: number
}

// 简单加密/解密（实际项目中应使用更安全的加密方式）
const encode = (data: string): string => {
  return btoa(encodeURIComponent(data))
}

const decode = (data: string): string => {
  try {
    return decodeURIComponent(atob(data))
  } catch {
    return data
  }
}

class Storage {
  private storage: globalThis.Storage
  private prefix: string

  constructor(storage: globalThis.Storage, prefix = 'curtain-') {
    this.storage = storage
    this.prefix = prefix
  }

  private getKey(key: string): string {
    return `${this.prefix}${key}`
  }

  // 设置存储
  set(key: string, value: any, options: StorageOptions = {}): boolean {
    try {
      const { encrypt = false, expire } = options

      const item: StorageItem = {
        data: value,
        timestamp: Date.now(),
        expire
      }

      let dataStr = JSON.stringify(item)

      if (encrypt) {
        dataStr = encode(dataStr)
      }

      this.storage.setItem(this.getKey(key), dataStr)
      return true
    } catch (error) {
      console.error('Storage set error:', error)
      return false
    }
  }

  // 获取存储
  get<T = any>(key: string, options: StorageOptions = {}): T | null {
    try {
      const { encrypt = false } = options
      let dataStr = this.storage.getItem(this.getKey(key))

      if (!dataStr) return null

      if (encrypt) {
        dataStr = decode(dataStr)
      }

      const item: StorageItem = JSON.parse(dataStr)

      // 检查是否过期
      if (item.expire && Date.now() - item.timestamp > item.expire) {
        this.remove(key)
        return null
      }

      return item.data
    } catch (error) {
      console.error('Storage get error:', error)
      return null
    }
  }

  // 移除存储
  remove(key: string): boolean {
    try {
      this.storage.removeItem(this.getKey(key))
      return true
    } catch (error) {
      console.error('Storage remove error:', error)
      return false
    }
  }

  // 清空所有存储（仅清空带前缀的）
  clear(): boolean {
    try {
      const keys = Object.keys(this.storage).filter(key =>
        key.startsWith(this.prefix)
      )

      keys.forEach(key => {
        this.storage.removeItem(key)
      })

      return true
    } catch (error) {
      console.error('Storage clear error:', error)
      return false
    }
  }

  // 检查是否存在
  has(key: string): boolean {
    return this.get(key) !== null
  }

  // 获取所有键
  keys(): string[] {
    return Object.keys(this.storage)
      .filter(key => key.startsWith(this.prefix))
      .map(key => key.substring(this.prefix.length))
  }

  // 获取存储大小（估算）
  size(): number {
    let size = 0
    for (const key in this.storage) {
      if (key.startsWith(this.prefix)) {
        size += key.length + (this.storage[key]?.length || 0)
      }
    }
    return size
  }

  // 清理过期数据
  cleanup(): number {
    let cleanedCount = 0
    const keys = this.keys()

    keys.forEach(key => {
      try {
        // 静默尝试获取，如果返回null说明已过期或格式错误，都会被清理
        if (this.get(key) === null) {
          cleanedCount++
        }
      } catch {
        // 忽略错误，继续下一个
      }
    })

    return cleanedCount
  }
}

// 创建存储实例
export const localStorage = new Storage(window.localStorage)
export const sessionStorage = new Storage(window.sessionStorage)

// 常用存储键名常量
export const STORAGE_KEYS = {
  // 用户相关
  USER_TOKEN: 'user-token',
  USER_INFO: 'user-info',
  USER_PREFERENCES: 'user-preferences',
  REMEMBER_ME: 'remember-me',
  REMEMBERED_USERNAME: 'remembered-username',
  REMEMBERED_PASSWORD: 'remembered-password',

  // 应用设置
  APP_THEME: 'app-theme',
  APP_LANGUAGE: 'app-language',
  APP_SIDEBAR: 'app-sidebar',

  // 购物车
  CART_ITEMS: 'cart-items',

  // 搜索历史
  SEARCH_HISTORY: 'search-history',

  // 浏览历史
  VIEW_HISTORY: 'view-history',

  // 临时数据
  TEMP_DESIGN: 'temp-design',
  TEMP_ORDER: 'temp-order'
} as const

// 特定业务存储操作
export const storageUtils = {
  // 用户相关
  setUserToken(token: string) {
    return localStorage.set(STORAGE_KEYS.USER_TOKEN, token, { encrypt: true })
  },

  getUserToken(): string | null {
    return localStorage.get(STORAGE_KEYS.USER_TOKEN, { encrypt: true })
  },

  removeUserToken() {
    return localStorage.remove(STORAGE_KEYS.USER_TOKEN)
  },

  // 记住我功能
  setRememberMe(remember: boolean) {
    return localStorage.set(STORAGE_KEYS.REMEMBER_ME, remember)
  },

  getRememberMe(): boolean {
    return localStorage.get(STORAGE_KEYS.REMEMBER_ME) || false
  },

  setRememberedUsername(username: string) {
    return localStorage.set(STORAGE_KEYS.REMEMBERED_USERNAME, username, { encrypt: true })
  },

  getRememberedUsername(): string | null {
    return localStorage.get(STORAGE_KEYS.REMEMBERED_USERNAME, { encrypt: true })
  },

  setRememberedPassword(password: string) {
    return localStorage.set(STORAGE_KEYS.REMEMBERED_PASSWORD, password, { encrypt: true })
  },

  getRememberedPassword(): string | null {
    return localStorage.get(STORAGE_KEYS.REMEMBERED_PASSWORD, { encrypt: true })
  },

  clearRememberedCredentials() {
    localStorage.remove(STORAGE_KEYS.REMEMBER_ME)
    localStorage.remove(STORAGE_KEYS.REMEMBERED_USERNAME)
    localStorage.remove(STORAGE_KEYS.REMEMBERED_PASSWORD)
  },

  // 搜索历史
  addSearchHistory(keyword: string, maxCount = 10) {
    const history: string[] = this.getSearchHistory()

    // 移除重复项
    const index = history.indexOf(keyword)
    if (index > -1) {
      history.splice(index, 1)
    }

    // 添加到开头
    history.unshift(keyword)

    // 限制数量
    if (history.length > maxCount) {
      history.splice(maxCount)
    }

    return localStorage.set(STORAGE_KEYS.SEARCH_HISTORY, history)
  },

  getSearchHistory(): string[] {
    return localStorage.get(STORAGE_KEYS.SEARCH_HISTORY) || []
  },

  clearSearchHistory() {
    return localStorage.remove(STORAGE_KEYS.SEARCH_HISTORY)
  },

  // 浏览历史
  addViewHistory(product: any, maxCount = 20) {
    const history: any[] = this.getViewHistory()

    // 移除重复项
    const index = history.findIndex(item => item.id === product.id)
    if (index > -1) {
      history.splice(index, 1)
    }

    // 添加到开头
    history.unshift({
      ...product,
      viewTime: Date.now()
    })

    // 限制数量
    if (history.length > maxCount) {
      history.splice(maxCount)
    }

    return localStorage.set(STORAGE_KEYS.VIEW_HISTORY, history)
  },

  getViewHistory(): any[] {
    return localStorage.get(STORAGE_KEYS.VIEW_HISTORY) || []
  },

  clearViewHistory() {
    return localStorage.remove(STORAGE_KEYS.VIEW_HISTORY)
  },

  // 应用设置
  setTheme(theme: string) {
    return localStorage.set(STORAGE_KEYS.APP_THEME, theme)
  },

  getTheme(): string | null {
    return localStorage.get(STORAGE_KEYS.APP_THEME)
  },

  setLanguage(language: string) {
    return localStorage.set(STORAGE_KEYS.APP_LANGUAGE, language)
  },

  getLanguage(): string | null {
    return localStorage.get(STORAGE_KEYS.APP_LANGUAGE)
  },

  // 临时数据（会话级别） - 购物车除外
  setTempData(key: string, data: any) {
    // 购物车使用localStorage以便持久化和跨标签页共享
    if (key === STORAGE_KEYS.CART_ITEMS) {
      return localStorage.set(key, data)
    }
    return sessionStorage.set(key, data, {
      expire: 30 * 60 * 1000 // 30分钟过期
    })
  },

  getTempData(key: string) {
    // 购物车使用localStorage以便持久化和跨标签页共享
    if (key === STORAGE_KEYS.CART_ITEMS) {
      return localStorage.get(key)
    }
    return sessionStorage.get(key)
  },

  removeTempData(key: string) {
    // 购物车使用localStorage以便持久化和跨标签页共享
    if (key === STORAGE_KEYS.CART_ITEMS) {
      return localStorage.remove(key)
    }
    return sessionStorage.remove(key)
  }
}

// 定期清理过期数据
if (typeof window !== 'undefined') {
  setInterval(() => {
    localStorage.cleanup()
    sessionStorage.cleanup()
  }, 10 * 60 * 1000) // 每10分钟清理一次
}
